// components/ui/Button.tsx
import { type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../lib/utils';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  children: ReactNode;
  isLoading?: boolean;
}

export function Button({
  variant = 'primary',
  size = 'md',
  children,
  isLoading,
  className,
  disabled,
  ...props
}: ButtonProps) {
  const baseStyles = cn(
    'inline-flex items-center justify-center gap-2',
    'font-body font-medium',
    'rounded-sm',
    'transition-all duration-fast',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none'
  );

  const variants = {
    primary: cn(
      'bg-primary text-text-inverse',
      'hover:bg-primary-hover hover:-translate-y-0.5',
      'active:bg-primary-active active:translate-y-0'
    ),
    secondary: cn(
      'bg-transparent text-text',
      'border-[1.5px] border-border-strong',
      'hover:bg-bg-subtle hover:border-text-muted',
      'active:bg-border-subtle'
    ),
    ghost: cn(
      'bg-transparent text-text-secondary',
      'hover:bg-bg-subtle hover:text-text',
      'active:bg-border-subtle'
    ),
    danger: cn(
      'bg-error text-text-inverse',
      'hover:brightness-110 hover:-translate-y-0.5',
      'active:brightness-90 active:translate-y-0'
    ),
  };

  const sizes = {
    sm: 'h-9 px-4 text-body-sm',
    md: 'h-11 px-5 text-body-md',
    lg: 'h-12 px-6 text-body-lg',
  };

  return (
    <button
      className={cn(baseStyles, variants[variant], sizes[size], className)}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? (
        <>
          <svg
            className="animate-spin h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span>Loading...</span>
        </>
      ) : (
        children
      )}
    </button>
  );
}
